/*
 * Okkto extras: little critters rising from behind the hero wave and up through the footer.
 *   bubbles (Regular)  - drift straight up with a gentle wobble
 *   bats    (Okktober) - flap and swoop their way up
 * Settings come from window.okktoExtras (snippets/okkto-personality.liquid).
 * Animated with GSAP (assets/gsap.min.js, loaded for the wave animation), with a CSS fallback.
 * Skipped for prefers-reduced-motion; pauses while off screen or the tab is hidden.
 */
(function () {
  'use strict';

  var cfg = window.okktoExtras || {};
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function rand(min, max) { return min + Math.random() * (max - min); }

  /* ------------------------------------------------------------------ */
  /* Critter types                                                       */
  /* ------------------------------------------------------------------ */
  var BAT_SVG =
    '<svg viewBox="0 0 120 60" aria-hidden="true" focusable="false">' +
      '<path class="okkto-bat__wing okkto-bat__wing--left" d="M56 26 C46 14 30 10 4 18 C10 22 12 28 10 34 C16 30 22 32 24 38 C28 33 34 33 38 38 C42 33 48 32 56 36 Z"/>' +
      '<path class="okkto-bat__wing okkto-bat__wing--right" d="M64 26 C74 14 90 10 116 18 C110 22 108 28 110 34 C104 30 98 32 96 38 C92 33 86 33 82 38 C78 33 72 32 64 36 Z"/>' +
      '<ellipse cx="60" cy="33" rx="7" ry="11"/>' +
      '<circle cx="60" cy="21" r="7"/>' +
      '<path d="M54 18 L54.5 8 L58.5 15 Z M66 18 L65.5 8 L61.5 15 Z"/>' +
    '</svg>';

  // Two leaf shapes (40x48): a lobed oak leaf and a pointed leaf, each with veins
  var LEAF_SHAPES = [
    '<path class="okkto-leaf__blade" d="M20 2 C24 4 25 8 23 10 C27 8 31 9 31 13 C30 16 27 17 25 17 C30 17 34 19 33 24 C32 28 28 28 26 27 C30 30 31 34 28 37 C25 39 23 37 22 36 L21 44 L19 44 L18 36 C17 37 15 39 12 37 C9 34 10 30 14 27 C12 28 8 28 7 24 C6 19 10 17 15 17 C13 17 10 16 9 13 C9 9 13 8 17 10 C15 8 16 4 20 2 Z"/>' +
    '<path class="okkto-leaf__vein" d="M20 6 L20 46 M20 15 L26 12 M20 15 L14 12 M20 23 L28 20 M20 23 L12 20 M20 31 L25 29 M20 31 L15 29"/>',
    '<path class="okkto-leaf__blade" d="M20 2 C30 10 36 22 32 34 C29 42 24 45 20 46 C16 45 11 42 8 34 C4 22 10 10 20 2 Z"/>' +
    '<path class="okkto-leaf__vein" d="M20 6 L20 48 M20 18 L28 13 M20 26 L30 20 M20 34 L28 30 M20 18 L12 13 M20 26 L10 20 M20 34 L12 30"/>'
  ];

  var CRITTERS = {
    bubbles: {
      max: 8,
      spawnEvery: [700, 2000],
      clusterChance: 0.25,
      sway: 18,                 // max side-to-side movement in px
      tripSeconds: [4.5, 8],
      heroSpeed: [47, 84],      // px/s in the hero (same pace as the original 4.5-8s trip, now taller)
      footerSpeed: [40, 65],    // px/s in the footer
      fade: [0.75, 0.98],       // fade out between these fractions of the trip
      footerFade: [0.55, 0.92], // footer keeps its original, earlier fade
      heroFill: 1,              // share of the space between wave and hero top/header to use
      heroFromPageTop: true,    // rise all the way up, beneath the overlaid header/nav
      sideChance: 0,            // share that enter from the screen sides
      create: function () {
        var size = rand(6, 18);
        var el = document.createElement('span');
        el.className = 'okkto-bubble';
        el.style.width = el.style.height = size + 'px';
        // extent: largest on-screen size it can take (here: just its size, scaled up to 1.1x)
        return { el: el, w: size, h: size, extent: size * 1.1, opacity: 0.9, speedFactor: 1 };
      },
      fly: function (gsap, el, c, sway) {
        var amount = rand(6, sway - 4);
        return [gsap.fromTo(el, { x: -amount }, {
          x: amount, duration: rand(1.2, 2.2), ease: 'sine.inOut', yoyo: true, repeat: -1
        })];
      },
      startScale: 0.6,
      endScale: 1.1
    },
    bats: {
      max: 5,
      spawnEvery: [1200, 3000],
      clusterChance: 0.35,      // bats like company
      sway: 70,
      maxTilt: 50,              // degrees of lean into a turn
      tripSeconds: [4, 6.5],
      footerSpeed: [70, 110],
      fade: [0.75, 0.98],       // stay visible for most of the climb
      heroFill: 1,
      heroFromPageTop: true,    // fly all the way up, beneath the overlaid header/nav
      // Footer: the footer wave's line never dips more than ~125px below the top of its canvas, so
      // stop 130px short of the canvas top and bats always fade out over the orange, never the white
      footerWaveClearance: 130,
      sideChance: 0.2,          // about 1 in 5 flies in from the left or right edge
      create: function () {
        var w = rand(24, 44);
        var depth = (w - 24) / 20; // 0 = small/far, 1 = big/near
        var el = document.createElement('span');
        el.className = 'okkto-bat';
        el.style.width = w + 'px';
        el.style.height = (w / 2) + 'px';
        el.innerHTML = BAT_SVG;
        // Each bat flaps at its own tempo
        var flap = rand(0.14, 0.22).toFixed(3) + 's';
        var delay = -rand(0, 0.2).toFixed(3) + 's';
        el.querySelectorAll('.okkto-bat__wing').forEach(function (wing) {
          wing.style.animationDuration = flap;
          wing.style.animationDelay = delay;
        });
        // extent: tilted (up to maxTilt), a 2:1 bat's bounding box stays within ~1.1x its width
        return { el: el, w: w, h: w / 2, extent: w * 1.1, opacity: 0.7 + depth * 0.3, speedFactor: 0.75 + depth * 0.4 };
      },
      fly: function (gsap, el, c, sway, climb, fromSide) {
        var alive = true;
        var leg = null;
        var side = Math.random() < 0.5 ? -1 : 1;
        var maxTilt = this.maxTilt;
        // Bats flying in from the side already cover a lot of ground, so they weave less
        var amp = fromSide ? sway * 0.4 : sway;

        // Irregular zigzag: dart to a random spot on the other side, then turn back, with varied
        // widths and timings so it flits rather than sways
        function nextLeg() {
          if (!alive) return;
          side = -side;
          var target = side * rand(amp * 0.35, amp);
          var dist = Math.abs(target - gsap.getProperty(el, 'x'));
          leg = gsap.to(el, {
            x: target,
            duration: Math.max(0.45, dist / rand(90, 160)),
            ease: 'sine.inOut',
            onComplete: nextLeg
          });
        }
        nextLeg();

        // Face the direction of travel: the tilt follows the bat's actual sideways movement (zigzag
        // plus any fly-in drift, which is animated on xPercent) against its climb, eased so it swings
        // through each turn and leans hard into each dart
        function horizontal() {
          return gsap.getProperty(el, 'x') + gsap.getProperty(el, 'xPercent') * c.w / 100;
        }
        var lastX = horizontal();
        var lastT = gsap.ticker.time;
        var tilt = 0;
        function steer() {
          var t = gsap.ticker.time;
          var dt = t - lastT;
          if (dt <= 0) return;
          var x = horizontal();
          var vx = (x - lastX) / dt;
          lastX = x;
          lastT = t;
          var target = Math.max(-maxTilt, Math.min(maxTilt, Math.atan2(vx, climb) * 180 / Math.PI));
          tilt += (target - tilt) * Math.min(1, dt * 10);
          gsap.set(el, { rotation: tilt });
        }
        gsap.ticker.add(steer);

        return [{
          kill: function () {
            alive = false;
            if (leg) leg.kill();
            gsap.ticker.remove(steer);
          }
        }];
      },
      startScale: 0.85,
      endScale: 1
    },
    leaves: {
      direction: 'down',        // fall from the top instead of rising from the bottom
      max: 7,
      spawnEvery: [900, 2200],
      clusterChance: 0.2,
      sway: 55,
      tripSeconds: [6, 10],
      footerSpeed: [35, 60],
      fade: [0.85, 0.98],       // in the hero they slip behind the wave before this anyway
      heroFill: 1,
      heroFromPageTop: true,    // start right at the top of the page (behind the overlaid header,
                                // which sits above the wave layer), not below the header
      // Footer: the top of the layer overlaps the white part of the footer wave, so fade in slowly
      // and stay faint at first, coming in fully once over the orange
      footerFadeIn: [3, 'power2.in'],
      sideChance: 0,
      // Colours from the Fall banner's leaves, weighted to the bright ones that stand out on its dark
      // brown (repeats = more common); the footer is orange, so it skips the orange tones
      palette: ['#f6c453', '#f6c453', '#ffb067', '#ee8a3c', '#e8613c', '#c8343f'],
      footerPalette: ['#f6c453', '#ffd98a', '#b3243c', '#8c1f3a', '#6b2e1a'],
      create: function (opts) {
        var w = rand(18, 34);
        var depth = (w - 18) / 16;
        var palette = (opts && opts.palette) || this.palette;
        var el = document.createElement('span');
        el.className = 'okkto-leaf';
        el.style.width = w + 'px';
        el.style.height = (w * 1.2) + 'px';
        el.style.color = palette[Math.floor(Math.random() * palette.length)];
        el.innerHTML = '<svg viewBox="0 0 40 48" aria-hidden="true" focusable="false">' +
          LEAF_SHAPES[Math.floor(Math.random() * LEAF_SHAPES.length)] + '</svg>';
        // extent: it can spin to any angle, so allow for its diagonal
        return { el: el, w: w, h: w * 1.2, extent: w * 1.57, opacity: 0.8 + depth * 0.2, speedFactor: 0.8 + depth * 0.35 };
      },
      fly: function (gsap, el, c, sway) {
        var svg = el.querySelector('svg');
        var swing = rand(1.4, 2.4);
        var amount = rand(sway * 0.45, sway - 6);
        var tweens = [];
        if (Math.random() < 0.25) {
          // Tumbler: spins as it falls, with a smaller drift
          tweens.push(gsap.fromTo(el, { x: -amount * 0.5 }, { x: amount * 0.5, duration: swing, ease: 'sine.inOut', yoyo: true, repeat: -1 }));
          tweens.push(gsap.fromTo(el, { rotation: rand(0, 360) }, { rotation: '+=' + (Math.random() < 0.5 ? 360 : -360), duration: rand(2.5, 4.5), ease: 'none', repeat: -1 }));
        } else {
          // Pendulum: sways side to side, rocking up at the end of each swing like a real leaf
          var rock = rand(25, 45);
          tweens.push(gsap.fromTo(el, { x: -amount }, { x: amount, duration: swing, ease: 'sine.inOut', yoyo: true, repeat: -1 }));
          tweens.push(gsap.fromTo(el, { rotation: rock }, { rotation: -rock, duration: swing, ease: 'sine.inOut', yoyo: true, repeat: -1 }));
        }
        // Every so often it flips over, showing its other side (on the inner SVG, so it doesn't
        // fight the rotation above)
        tweens.push(gsap.fromTo(svg, { scaleX: 1 }, { scaleX: -1, duration: rand(0.6, 1.1), ease: 'sine.inOut', yoyo: true, repeat: -1, repeatDelay: rand(1, 3), delay: rand(0, 2) }));
        return tweens;
      },
      startScale: 1,
      endScale: 1
    }
  };

  /* ------------------------------------------------------------------ */
  /* Layers                                                              */
  /* ------------------------------------------------------------------ */
  function init() {
    var type = CRITTERS[cfg.critters];
    if (!type || reduceMotion) return;

    var heroWave = document.getElementById('hero-wave-holder');
    if (heroWave) {
      var heroLayer = critterLayer(heroWave, 'okkto-critters--hero', type, { speed: type.heroSpeed });
      sizeHeroLayer(heroWave, heroLayer, type);
      onLayoutChange(heroLayer, function () { sizeHeroLayer(heroWave, heroLayer, type); });
    }

    var footer = document.querySelector('.site-footer .footer-holder');
    if (footer) {
      // Footer critters keep a steady pace, so the taller layer means they're visible for longer
      var footerLayer = critterLayer(footer, 'okkto-critters--footer', type, {
        speed: type.footerSpeed,
        fade: type.footerFade,
        palette: type.footerPalette,
        fadeIn: type.footerFadeIn
      });
      sizeFooterLayer(footer, footerLayer, type);
      onLayoutChange(footerLayer, function () { sizeFooterLayer(footer, footerLayer, type); });
    }
  }

  // Re-size a layer after the window resizes. Critters already in flight were aimed at the old
  // size, so if the layer really changed they quickly fade out (new ones use the new size). Mobile
  // address-bar show/hide fires resize too, but doesn't change the layer, so nothing happens then.
  function onLayoutChange(layer, resize) {
    var timer = null;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var before = layer.offsetTop + ':' + layer.offsetHeight + ':' + layer.offsetWidth;
        resize();
        if (layer.offsetTop + ':' + layer.offsetHeight + ':' + layer.offsetWidth === before) return;
        Array.prototype.forEach.call(layer.children, function (el) {
          if (window.gsap) {
            window.gsap.to(el, { opacity: 0, duration: 0.25, overwrite: 'auto' });
          } else {
            el.remove();
          }
        });
      }, 150);
    });
  }

  // The hero layer sits behind the wave canvas and reaches up over the hero section above it,
  // but stops short of the hero's top and of any header overlaid on the hero (critters never
  // drift over the logo or menu)
  function sizeHeroLayer(holder, layer, type) {
    var waveSection = holder.closest('.shopify-section');
    var hero = waveSection && waveSection.previousElementSibling;
    var holderRect = holder.getBoundingClientRect();
    var top;
    if (type.heroFromPageTop) {
      // Reach right to the top of the page (the overlaid header sits above this layer)
      top = -window.scrollY;
    } else {
      top = hero ? hero.getBoundingClientRect().top : holderRect.top - 300;
      var header = document.querySelector('.header-wrapper');
      if (header) top = Math.max(top, header.getBoundingClientRect().bottom);
    }
    var height = Math.round(Math.max((holderRect.bottom - top) * type.heroFill, holderRect.height));
    layer.style.height = height + 'px';
  }

  // The footer layer reaches up from the footer background through the footer wave above it,
  // so critters rise through the wave and fade out around its crest (or, with footerWaveClearance,
  // that many px below the top of the wave, so they stay over its solid colour)
  function sizeFooterLayer(holder, layer, type) {
    var wave = holder.parentElement && holder.parentElement.querySelector('.wave-bottom');
    var extend = wave ? holder.getBoundingClientRect().top - wave.getBoundingClientRect().top : 0;
    extend -= type.footerWaveClearance || 0;
    layer.style.top = -Math.max(Math.round(extend), 0) + 'px';
  }

  // opts.speed: [min, max] rise speed in px/s (default: the critter's tripSeconds regardless of height)
  // opts.palette: colours for critters that use one; opts.fadeIn: [seconds, ease] for the fade-in;
  // opts.fade: [start, end] fade-out fractions overriding the critter's own
  function critterLayer(host, cls, type, opts) {
    opts = opts || {};
    var layer = document.createElement('div');
    layer.className = 'okkto-critters ' + cls;
    layer.setAttribute('aria-hidden', 'true');
    host.classList.add('okkto-critters-host');
    host.appendChild(layer);

    var visible = false;

    function spawn() {
      if (visible && !document.hidden && layer.childElementCount < type.max) {
        var c = type.create(opts);
        var el = c.el;
        var falling = type.direction === 'down';
        // The layer clips its contents, so keep every critter fully inside it, using its largest
        // on-screen size (c.extent, e.g. when tilted): at least `sway` px from the sides, and stop
        // just before the far edge (it is fully faded by then), keeping a few px spare.
        // Rising critters start 20px below the bottom edge (CSS); falling ones start just inside the
        // top edge (and fade in there), so `rise` is simply the distance travelled.
        var sideRoom = type.sway + (c.extent - c.w) / 2 + 4;
        var rise = falling
          ? Math.max(layer.offsetHeight - c.extent - 8, 40)
          : Math.max(layer.offsetHeight + 20 - c.h / 2 - c.extent * type.endScale / 2 - 6, 40);
        var duration = opts.speed
          ? rise / (rand(opts.speed[0], opts.speed[1]) * c.speedFactor)
          : rand(type.tripSeconds[0], type.tripSeconds[1]) / c.speedFactor;
        var entry = null;

        if (window.gsap && Math.random() < type.sideChance) {
          // Fly in from just past the left or right screen edge, partway up, angling inward while
          // climbing to the same top limit as the others. Being cut off at the screen edge on the way
          // in is natural; the top edge still never cuts it off visibly.
          // It drifts inward at a constant sideways speed similar to the other bats' movement, so it
          // doesn't rush in and brake.
          var fromLeft = Math.random() < 0.5;
          var startUp = rand(0.25, 0.6) * rise;
          duration = Math.max(duration * (rise - startUp) / rise, 2.5);
          var drift = Math.min(rand(50, 90) * duration, layer.clientWidth * 0.6);
          entry = {
            startUp: startUp,
            driftPct: (fromLeft ? drift : -drift) / c.w * 100
          };
          el.style.left = (fromLeft ? -c.extent - 10 : layer.clientWidth + 10) + 'px';
        } else {
          el.style.left = rand(sideRoom, Math.max(sideRoom, layer.clientWidth - c.w - sideRoom)) + 'px';
        }
        layer.appendChild(el);

        if (window.gsap) {
          fly(el, c, duration, rise, entry);
        } else if (falling) {
          // CSS fallback (single smooth fall, see okkto-personality.css)
          el.classList.add('okkto-critter--css-fall');
          el.style.setProperty('--from', ((c.extent - c.h) / 2 + 2) + 'px');
          el.style.setProperty('--to', ((c.extent - c.h) / 2 + 2 + rise) + 'px');
          el.style.animationDuration = duration + 's';
          el.addEventListener('animationend', function () { el.remove(); });
        } else {
          // CSS fallback (single smooth rise, see okkto-personality.css)
          el.classList.add('okkto-critter--css');
          el.style.setProperty('--rise', -rise + 'px');
          el.style.animationDuration = duration + 's';
          el.addEventListener('animationend', function () { el.remove(); });
        }
        // Sometimes a little group
        if (Math.random() < type.clusterChance) setTimeout(spawn, rand(150, 450));
      }
      setTimeout(spawn, rand(type.spawnEvery[0], type.spawnEvery[1]));
    }

    // GreenSock: one continuous, steady rise (or fall), with the critter's own side-to-side motion
    // layered on top, so it never stalls mid-way
    function fly(el, c, duration, rise, entry) {
      var gsap = window.gsap;
      var falling = type.direction === 'down';
      var fade = opts.fade || type.fade;
      var startY, endY;
      if (falling) {
        startY = (c.extent - c.h) / 2 + 2; // fully inside the top edge even when spun
        endY = startY + rise;
      } else {
        startY = entry ? -entry.startUp : 0;
        endY = -rise;
      }
      var extras = type.fly(gsap, el, c, type.sway, Math.abs(endY - startY) / duration, !!entry);
      var travel = { y: endY, duration: duration, ease: 'none' };
      var from = { y: startY };
      if (type.startScale !== type.endScale) {
        from.scale = type.startScale;
        travel.scale = type.endScale;
      }
      var tl = gsap.timeline({
        onComplete: function () {
          extras.forEach(function (t) { t.kill(); });
          el.remove();
        }
      })
        .fromTo(el, from, travel, 0)
        // Side entrants arrive already visible (they come in from off screen); risers fade in while
        // still hidden behind the wave; fallers fade in gently just below the top edge
        .fromTo(el, { opacity: entry ? c.opacity : 0 }, {
          opacity: c.opacity,
          duration: opts.fadeIn ? Math.min(opts.fadeIn[0], duration * 0.4) : (falling ? 1 : 0.6),
          ease: opts.fadeIn ? opts.fadeIn[1] : 'power1.out'
        }, 0)
        // Fully faded by fade[1] of the trip, i.e. a little before it reaches the clipping edge
        .to(el, {
          opacity: 0,
          duration: duration * (fade[1] - fade[0]),
          ease: 'sine.in'
        }, duration * fade[0]);
      if (entry) {
        // Drift inward at a steady pace on xPercent (the zigzag uses x)
        tl.fromTo(el, { xPercent: 0 }, { xPercent: entry.driftPct, duration: duration, ease: 'none' }, 0);
      }
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }).observe(host);
    } else {
      visible = true;
    }
    spawn();
    return layer;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
